import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  getCommunications,
  deleteCommunication,
  updateCommTaskStatus,
  completeCommFollowUp,
  getStaff,
  getStudents,
  getCommunicationTypes,
} from "../../lib/api";
import CommunicationDialog from "../communications/CommunicationDialog";
import { TYPE_LABELS } from "../communications/constants";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
}

export function CommunicationsTab({ studentId, studentName, refreshKey }) {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const res = await getCommunications({ student_id: studentId });
      setItems(res.data);
    } catch {
      toast.error("Failed to load communications");
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [staffRes, studRes, typesRes] = await Promise.all([getStaff(), getStudents("current", true), getCommunicationTypes()]);
        if (!alive) return;
        setStaff(staffRes.data);
        setStudents(studRes.data.filter((s) => s.status === "current"));
        setCustomTypes(typesRes.data);
        await reload();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  // Reload when a save happens elsewhere (e.g. the student-header "+ Log Communication"
  // button) so the new entry appears without a page reload.
  useEffect(() => {
    if (refreshKey) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const onAdd = () => { setEditing(null); setDialogOpen(true); };
  const onEdit = (c) => { setEditing(c); setDialogOpen(true); };
  const onDelete = async (c) => {
    if (!window.confirm("Delete this communication?")) return;
    try { await deleteCommunication(c.id); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };
  const onToggleTask = async (commId, t) => {
    const next = t.status === "done" ? "open" : "done";
    try { await updateCommTaskStatus(commId, t.id, next); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const onFollowUpDone = async (commId) => {
    try { await completeCommFollowUp(commId); toast.success("Follow-up complete"); reload(); }
    catch { toast.error("Failed"); }
  };

  const typeLabel = (t) => TYPE_LABELS[t] || customTypes.find((c) => c.id === t)?.label || t;

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00305E]" /></div>;

  return (
    <div className="space-y-4" data-testid="student-comm-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Communications Log</CardTitle>
              <CardDescription>{items.length} log{items.length === 1 ? "" : "s"} for {studentName}</CardDescription>
            </div>
            <Button onClick={onAdd} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="student-add-comm-btn">+ Log Communication/Task</Button>
          </div>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No communications logged for this student yet.</p>
          ) : (
            <div className="space-y-3">
              {items.map((c) => {
                const isTaskOnly = c.direction === "task_only";
                const headline = isTaskOnly ? (c.subject || "Task") : (c.contact_name || "—");
                return (
                <div key={c.id} className="border rounded-md p-3 space-y-2" data-testid={`student-comm-${c.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline">{typeLabel(c.type)}</Badge>
                        {isTaskOnly ? (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700">★ Task</Badge>
                        ) : (
                          <Badge variant="outline" className={c.direction === "incoming" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}>
                            {c.direction === "incoming" ? "↓ Incoming" : "↑ Outgoing"}
                          </Badge>
                        )}
                        {c.visibility === "only_me" && <Badge className="bg-slate-200 text-slate-800">Only Me</Badge>}
                        {c.visibility === "private" && <Badge className="bg-gray-200 text-gray-800">Private</Badge>}
                        {c.visibility === "clinical" && <Badge className="bg-purple-100 text-purple-800">Clinical</Badge>}
                        {c.system_generated && <Badge className="bg-indigo-100 text-indigo-800" data-testid={`student-comm-automated-${c.id}`}>Automated</Badge>}
                      </div>
                      <div className="font-medium">{headline} {!isTaskOnly && <span className="text-xs text-muted-foreground">{c.contact_role && `· ${c.contact_role}`}</span>}</div>
                      <div className="text-xs text-muted-foreground">{fmt(c.occurred_at)} · by {c.created_by_name || "—"}</div>
                    </div>
                    <div className="flex gap-1">
                      {!c.system_generated && <Button variant="ghost" size="sm" onClick={() => onEdit(c)} data-testid={`student-comm-edit-${c.id}`}>Edit</Button>}
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onDelete(c)} data-testid={`student-comm-delete-${c.id}`}>Delete</Button>
                    </div>
                  </div>
                  {!isTaskOnly && c.subject && <div className="text-sm font-semibold">{c.subject}</div>}
                  {c.content && <div className="text-sm text-gray-700 whitespace-pre-wrap">{c.content}</div>}
                  {c.tasks && c.tasks.length > 0 && (
                    <div className="bg-gray-50 rounded p-2 space-y-1">
                      {c.tasks.map((t) => (
                        <div key={t.id} className="flex items-start gap-2 text-sm">
                          <input type="checkbox" checked={t.status === "done"} onChange={() => onToggleTask(c.id, t)} className="mt-0.5" />
                          <div className="flex-1">
                            <div className={t.status === "done" ? "line-through text-muted-foreground" : ""}>{t.description}</div>
                            <div className="text-xs text-muted-foreground">→ {t.assignee_name || "Unassigned"}{t.due_date && ` · due ${t.due_date}`}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {c.follow_up_date && (
                    <div className={`text-sm rounded p-2 border ${c.follow_up_completed ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <span className="font-semibold">Follow-up: </span>{c.follow_up_date}
                          {c.follow_up_notes && <span className="text-xs text-muted-foreground"> — {c.follow_up_notes}</span>}
                        </div>
                        {!c.follow_up_completed && <Button size="sm" variant="outline" onClick={() => onFollowUpDone(c.id)}>Mark done</Button>}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <CommunicationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        staff={staff}
        students={students}
        customTypes={customTypes}
        defaultStudentId={studentId}
        onSaved={() => { setDialogOpen(false); reload(); getCommunicationTypes().then((r) => setCustomTypes(r.data)).catch(() => {}); }}
      />
    </div>
  );
}
